import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LogIn, LogOut, Clock, CalendarPlus, CheckCircle2, Plus } from "lucide-react";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { portalApi, getPortalToken, setPortalToken } from "../lib/api";
import { LEAVE_TYPES } from "../mock";
import { useToast } from "../hooks/use-toast";

const statusStyle = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };

function Login({ code, orgName, onDone }) {
  const [empNo, setEmpNo] = useState("");
  const [surname, setSurname] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { const d = await portalApi.login(code, empNo, surname); setPortalToken(d.token); onDone(); }
    catch (err) { setError(err?.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-brand-mist flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-100 shadow-xl p-8">
        <div className="flex justify-center mb-5"><Logo /></div>
        <h1 className="font-display font-bold text-xl text-brand-navy text-center">{orgName || "Employee Portal"}</h1>
        <p className="text-slate-500 text-sm text-center mt-1 mb-6">Sign in to request leave & clock in.</p>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Employee number</Label><Input value={empNo} onChange={(e) => setEmpNo(e.target.value)} placeholder="e.g. KTC-001" required /></div>
          <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Surname</Label><Input value={surname} onChange={(e) => setSurname(e.target.value)} placeholder="e.g. Morake" required /></div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-11"><LogIn className="w-4 h-4 mr-1" />{loading ? "Signing in..." : "Sign in"}</Button>
        </form>
      </div>
    </div>
  );
}

export default function Portal() {
  const { code } = useParams();
  const { toast } = useToast();
  const [orgName, setOrgName] = useState("");
  const [authed, setAuthed] = useState(!!getPortalToken());
  const [me, setMe] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [today, setToday] = useState({});
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ leave_type: "Annual", start_date: "", end_date: "", reason: "" });

  useEffect(() => { portalApi.org(code).then((d) => setOrgName(d.organization_name)).catch(() => {}); }, [code]);

  const load = async () => {
    try {
      const [m, l, t] = await Promise.all([portalApi.me(), portalApi.listLeave(), portalApi.today()]);
      setMe(m); setLeaves(l); setToday(t);
    } catch (e) { setPortalToken(null); setAuthed(false); }
  };
  useEffect(() => { if (authed) load(); }, [authed]);

  const clock = async () => {
    try { const r = await portalApi.clock(); toast({ title: r.action === "clock_in" ? "Clocked in" : "Clocked out", description: r.action === "clock_out" ? `Hours today: ${r.hours}` : `At ${r.time}` }); await load(); }
    catch (e) { toast({ title: "Could not clock", variant: "destructive" }); }
  };

  const submitLeave = async (e) => {
    e.preventDefault();
    try { await portalApi.createLeave(form); setDialog(false); setForm({ leave_type: "Annual", start_date: "", end_date: "", reason: "" }); await load(); toast({ title: "Leave requested", description: "Your manager will review it." }); }
    catch (err) { toast({ title: "Could not submit", description: err?.response?.data?.detail || "Try again", variant: "destructive" }); }
  };

  const logout = () => { setPortalToken(null); setAuthed(false); setMe(null); };

  if (!authed) return <Login code={code} orgName={orgName} onDone={() => setAuthed(true)} />;

  const annual = me?.balances?.find((b) => b.type === "Annual") || {};
  const sick = me?.balances?.find((b) => b.type === "Sick") || {};

  return (
    <div className="min-h-screen bg-brand-mist">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-5 h-[68px] flex items-center justify-between">
          <Logo size="sm" showTagline={false} />
          <button onClick={logout} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-blue"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8">
        <div className="flex items-center gap-4 mb-6">
          {me?.employee?.avatar ? <img src={me.employee.avatar} alt="" className="w-14 h-14 rounded-full object-cover" /> : <div className="w-14 h-14 rounded-full bg-brand-light text-brand-blue flex items-center justify-center font-semibold text-lg">{me?.employee?.first_name?.[0]}</div>}
          <div>
            <h1 className="font-display font-extrabold text-2xl text-brand-navy">Hi, {me?.employee?.first_name} 👋</h1>
            <p className="text-slate-500 text-sm">{me?.employee?.job_title} · {orgName}</p>
          </div>
        </div>

        {/* Clock + balances */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <div className="text-xs text-slate-500 mb-1">Annual leave left</div>
            <div className="font-display font-extrabold text-3xl text-brand-navy">{annual.remaining ?? "—"}<span className="text-base text-slate-400"> / {annual.entitlement ?? "—"}</span></div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <div className="text-xs text-slate-500 mb-1">Sick leave left</div>
            <div className="font-display font-extrabold text-3xl text-brand-navy">{sick.remaining ?? "—"}<span className="text-base text-slate-400"> / {sick.entitlement ?? "—"}</span></div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-5 flex flex-col justify-between">
            <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Today {today.clock_in ? `· in ${today.clock_in}` : ""}{today.clock_out ? ` · out ${today.clock_out}` : ""}</div>
            <Button onClick={clock} className={`rounded-lg h-10 ${today.clock_in && !today.clock_out ? "bg-red-500 hover:bg-red-600" : "bg-brand-blue hover:bg-brand-bluedark"} text-white`}>
              {today.clock_in && !today.clock_out ? "Clock out" : today.clock_out ? "Done for today" : "Clock in"}
            </Button>
          </div>
        </div>

        {/* Leave requests */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg text-brand-navy">My leave requests</h2>
          <Button onClick={() => setDialog(true)} className="bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-10 px-4"><Plus className="w-4 h-4 mr-1" /> Request leave</Button>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-50">
          {leaves.length === 0 && <div className="py-12 text-center text-slate-400"><CalendarPlus className="w-8 h-8 mx-auto mb-2 text-slate-300" />No requests yet.</div>}
          {leaves.map((l) => (
            <div key={l.id} className="flex items-center gap-3 px-5 py-3.5">
              <div className="flex-1">
                <div className="font-semibold text-brand-navy">{l.leave_type} · {l.days} day{l.days > 1 ? "s" : ""}</div>
                <div className="text-xs text-slate-500">{l.start_date} → {l.end_date}{l.reason ? ` · ${l.reason}` : ""}</div>
              </div>
              <Badge className={`${statusStyle[l.status]} hover:bg-current/10 capitalize`}>{l.status}</Badge>
            </div>
          ))}
        </div>
      </main>

      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDialog(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md p-6">
            <h3 className="font-display font-bold text-xl text-brand-navy mb-1">Request leave</h3>
            <p className="text-sm text-slate-500 mb-4">Submit a request for your manager to review.</p>
            <form onSubmit={submitLeave} className="space-y-4">
              <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Leave type</Label>
                <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAVE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required /></div>
                <div className="grid gap-1.5"><Label className="text-xs text-slate-500">End</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required /></div>
              </div>
              <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Reason</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Optional" /></div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" className="flex-1 rounded-lg" onClick={() => setDialog(false)}>Cancel</Button>
                <Button type="submit" className="flex-1 bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg">Submit</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
